import React, { useEffect, useState, useContext } from "react";
import { useNavigate, Routes, Route } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";
import { ThemeContext } from "./theme";
import { LanguageContext } from "./LanguageContext";

import {
  User,
  LogOut,
  Bell,
  Settings,
  FileText,
  Home,
  Sun,
  Moon,
  DollarSign,
  Clock,
  Coffee,
  Shield,
  BarChart3,
  AlertTriangle,
  UsersRound
} from "lucide-react";



// Import page components
import ProfessionalProfile from "./(User)Profile_Final/ProfilePage.jsx";
import Mo3tiDocumentsPage from "./(Employee)SelfService";
import Mo3tiAttendancePage from "./(User)attendance_page";
import Mo3tiLeavePage from "./(User)leave_Managment";
import Mo3tiSettingsPage from "./settings_Final";
import Mo3tiSalaryPage from "./(User)salary_Final";

import Calendar from './Calendar.js';
import SidebarWithTopbar from "./sidebar3.js";
import { mo3tiThemes } from "./mo3tiThemes";
import ViewTypeSwitcher from '../components/ViewTypeSwitcher';
import EscalationPage from "./escalation.js";
import GroupsManagement from "./groupsManagement.js";
import e from "cors";

// ===============================================
// TRANSLATIONS - الترجمات
// ===============================================
const translations = {
  en: {
    // Navigation
    profile: "Profile",
    salaryBenefits: "Salary ",
    attendance: "Attendance",
    leaveManagement: "Leave Management",
    employeeSelfServices: " Self Services",
    calendar: "Calendar",
    settingsLocked: "Settings ",
    escalation: "Escalation",
    groups: "Groups",

    // Loading
    loadingPermissions: "Loading permissions...",

    // Tooltips
    darkModeTooltip: "Switch to Dark Mode",
    lightModeTooltip: "Switch to Light Mode",

    // Common
    loading: "Loading...",
  },
  ar: {
    // Navigation
    profile: "الملف الشخصي",
    salaryBenefits: "الراتب ",
    attendance: "الحضور",
    leaveManagement: " الإجازات",
    employeeSelfServices: "الخدمات الذاتية ",
    calendar: "التقويم",
    settingsLocked: "الإعدادات ",
    escalation: "الشكاوى",
    groups: "المجموعات",

    // Loading
    loadingPermissions: "جاري تحميل الصلاحيات...",

    // Tooltips
    darkModeTooltip: "تبديل إلى الوضع المظلم",
    lightModeTooltip: "تبديل إلى الوضع الفاتح",

    // Common
    loading: "جاري التحميل...",
  }
};

// ===============================================
// MAIN COMPONENT - المكون الرئيسي
// ===============================================
const Mo3tiUserProfile = () => {
  // ============ HOOKS AND STATE ============
  const { user, logout } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const { theme, setTheme } = useContext(ThemeContext);
  const { language, toggleLanguage } = useContext(LanguageContext);

  // Language and direction
  const isRTL = language === 'ar';
  const t = translations[language];

  // Apply RTL to document
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [isRTL, language]);

  // State management
  const [notifications, setNotifications] = useState(3);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(true);

  // Current page state
  const [currentPage, setCurrentPage] = useState('professionalProfile');

  // View type state - unified key with DashboardManager
  const [viewType, setViewType] = useState(() => {
    const savedViewType = localStorage.getItem('managerViewType');
    return savedViewType || 'user';
  });

  // Reset currentPage when viewType changes
  useEffect(() => {
    setCurrentPage('professionalProfile');
  }, [viewType]);

  // ============ HELPER FUNCTIONS ============
  const handleViewTypeChange = (type) => {
    setViewType(type);

    // Navigation logic based on view type
    if (type === 'user') {
      // If user has admin role or admin level, go to userprofileadmin, otherwise stay in userprofilemanager
      if ((profile?.role || localStorage.getItem('role')) === 'admin' || hasAdminLevel()) {
        navigate('/userprofileadmin/professionalProfile');
      } else {
        navigate('../userprofilemanager/professionalProfile');
      }
    } else if (type === 'manager') {
      navigate('/DashboardManager/dashboard');

    } else if (type === 'admin') {
      // If user has admin role, go to userprofileadmin, otherwise go to dashboard
      if ((profile?.role || localStorage.getItem('role')) === 'admin' || hasAdminLevel()) {
        navigate('/userprofileadmin/professionalProfile');
      } else {
        navigate('/dashboard');
      }
    }
  };

  const hasAdminLevel = () => {
    try {
      const adminLevelData = localStorage.getItem('adminLevel');
      if (adminLevelData) {
        const adminLevel = JSON.parse(adminLevelData);
        return adminLevel && (adminLevel.modules?.length > 0 || adminLevel.adminLevelId);
      }
    } catch (error) {
      console.error('Error checking admin level:', error);
      return false;
    }
    return false;
  };

  const createAvatar = (name) => {
    if (!name) return "UN";
    const parts = name.split(" ");
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
  };

  // ============ DATA CONFIGURATION ============
  const currentTheme = isDarkMode ? mo3tiThemes.dark : mo3tiThemes.light;

  // Navigation menu items with translations
  const navItems = [
    {
      icon: User,
      text: t.profile,
      page: "professionalProfile"
    },
    {
      icon: DollarSign,
      text: t.salaryBenefits,
      page: "salary",
    },
    {
      icon: AlertTriangle,
      text: t.escalation,
      page: "escalation",
    },
    {
      icon: UsersRound,
      text: t.groups,
      page: "groups",
    },
    {
      icon: Clock,
      text: t.attendance,
      page: "attendance",
    },
    {
      icon: FileText,
      text: t.employeeSelfServices,
      page: "employee-services",
    },
    {
      icon: Home,
      text: t.calendar,
      page: "calendar",
    },
    {
      icon: Coffee,
      text: t.leaveManagement,
      page: "leave-management",
    },
    {
      icon: Settings,
      text: t.settingsLocked,
      page: "settings",
    },
  ];

  // Employee data — profile (from /users/me) is source of truth;
  // localStorage serves as an immediate fallback (populated at login).
  const _phone     = profile?.employeePhone         || localStorage.getItem("employeePhone")         || "";
  const _emergency = profile?.emergencyPhoneNumber  || localStorage.getItem("emergencyPhoneNumber")  || "";
  const _name      = profile?.employeeName          || localStorage.getItem("employeeName")          || "موظف غير محدد";
  const employeeData = {
    id:               profile?.infinityCode                  || localStorage.getItem("infinityCode")                  || "EMP001",
    name:             _name,
    position:         profile?.employeeType                  || localStorage.getItem("employeeType")                  || "--",
    department:       profile?.employeeType                  || localStorage.getItem("employeeType")                  || "--",
    email:            profile?.employeeEmail                 || localStorage.getItem("employeeEmail")                 || localStorage.getItem("email") || "employee@company.com",
    phone:            _phone ? "+20" + _phone : "--",
    address:          profile?.address                       || localStorage.getItem("address")                       || localStorage.getItem("location") || "--",
    joinDate:         profile?.hiringDate                    || localStorage.getItem("hiringDate")                    || null,
    employeeId:       profile?.infinityCode                  || localStorage.getItem("infinityCode")                  || "EMP001",
    manager:          profile?.firstLineManagerName          || localStorage.getItem("firstLineManagerName")          || "--",
    status:           profile?.employeeState                 || localStorage.getItem("employeeState")                 || "Active",
    avatar:           createAvatar(_name),
    birthDate:        profile?.DoB                           || localStorage.getItem("DoB")                           || null,
    nationalId:       profile?.nationalId                    || localStorage.getItem("nationalId")                    || "--",
    emergencyContact: _emergency ? "+20" + _emergency : "--",
    workLocation:     profile?.location                      || localStorage.getItem("location")                      || "--",
    firstLevelManager: profile?.firstLineManagerInfinityCode || localStorage.getItem("firstLineManagerInfinityCode") || "--",
  };

  // ============ EVENT HANDLERS ============
  const handleLogout = () => {
    try {
      logout();
      try {
        localStorage.removeItem('nav_admin');
        localStorage.removeItem('nav_manager');
        localStorage.removeItem('nav_employee');
      } catch (e) { }
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login");
    }
  };

  const handleSwitchManager = () => {
    try {
      // Clear the saved page when switching
      localStorage.removeItem('adminUserProfilePage');
      // Navigate to dashboard root
      navigate("/DashboardManager/dashboard");
    } catch (error) {
      console.error("switch error:", error);
      navigate("/userprofilemanager");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    navigate(`/userprofilemanager/${page}`);
  };

  const toggleTheme = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    if (setTheme) {
      setTheme(newDarkMode ? "dark" : "light");
    }
  };

  // ============ CREATE PAGE PROPS ============
  const pageProps = {
    employeeData,
    theme: currentTheme,
    isDarkMode
  };

  // ============ RENDER THEME TOGGLE ============
  const renderThemeToggle = () => (
    <button
      style={{
        padding: "8px",
        borderRadius: "12px",
        cursor: "pointer",
        color: theme === "light" ? "#0f172a" : "white",
        background: theme === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)",
        border: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.3s ease"
      }}
      onClick={toggleTheme}
      title={theme === "light" ? t.darkModeTooltip : t.lightModeTooltip}
    >
      {theme === "light" ? <Moon size={20} /> : <Sun size={20} />}
    </button>
  );

  // ============ RENDER LANGUAGE TOGGLE ============
  const renderLanguageToggle = () => (
    <button
      style={{
        padding: "8px 12px",
        borderRadius: "12px",
        cursor: "pointer",
        color: theme === "light" ? "#0f172a" : "white",
        background: theme === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)",
        border: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.3s ease",
        fontWeight: "600",
        fontSize: "14px"
      }}
      onClick={toggleLanguage}
      title={language === 'ar' ? 'Switch to English' : 'التبديل للعربية'}
    >
      {language === 'ar' ? 'EN' : 'ع'}
    </button>
  );

  // ============ STYLES ============
  const containerStyles = {
    minHeight: "100vh",
    background: currentTheme?.background?.gradient || "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    fontFamily: currentTheme?.fonts?.primary || "Arial, sans-serif",
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s ease",
    direction: isRTL ? "rtl" : "ltr",
  };

  const backgroundOverlayStyles = currentTheme?.background?.overlay || {};
  const decorativeContainerStyles = currentTheme?.background?.decorative?.container || {};
  const circle1Styles = currentTheme?.background?.decorative?.circle1 || {};
  const circle2Styles = currentTheme?.background?.decorative?.circle2 || {};

  const globalStyles = `
    * { 
      box-sizing: border-box; 
    }
    
    body {
      margin: 0;
      padding: 0;
      font-family: ${isRTL ? "'Cairo', 'Tajawal', 'Segoe UI', " : ""}${currentTheme?.fonts?.primary || "Arial, sans-serif"};
      direction: ${isRTL ? 'rtl' : 'ltr'};
    }
    
    input::placeholder { 
      color: ${currentTheme?.colors?.textSecondary || "#666"}; 
    }
    
    ::-webkit-scrollbar { 
      width: 8px; 
    }
    
    ::-webkit-scrollbar-track { 
      background: ${currentTheme?.colors?.scrollbar?.track || "#f1f1f1"}; 
      border-radius: 4px; 
    }
    
    ::-webkit-scrollbar-thumb { 
      background: ${currentTheme?.colors?.scrollbar?.thumb || "#c1c1c1"}; 
      border-radius: 4px; 
    }
    
    ::-webkit-scrollbar-thumb:hover { 
      background: ${currentTheme?.colors?.scrollbar?.thumbHover || "#a8a8a8"}; 
    }
    
    /* Responsive Design */
    @media (max-width: 768px) {
      .mo3ti-sidebar { 
        width: 100% !important; 
        ${isRTL ? 'right' : 'left'}: ${sidebarOpen ? "0" : "-100%"} !important; 
        z-index: 1000;
      }
      
      .mo3ti-main { 
        ${isRTL ? 'margin-right' : 'margin-left'}: 0 !important; 
      }
      
      .mo3ti-topbar { 
        padding: 12px 16px !important; 
      }
      
      .mo3ti-content { 
        padding: 20px 16px !important; 
      }
      
      .mo3ti-search { 
        width: 200px !important; 
      }
    }
    
    @media (max-width: 480px) {
      .mo3ti-content { 
        padding: 16px 12px !important; 
      }
      
      .mo3ti-search { 
        width: 150px !important; 
      }
    }
    
    /* RTL Specific Adjustments */
    [dir="rtl"] .mo3ti-sidebar {
      ${isRTL ? 'right' : 'left'}: 0;
      ${isRTL ? 'left' : 'right'}: auto;
    }
    
    [dir="rtl"] .mo3ti-main {
      ${isRTL ? 'margin-right' : 'margin-left'}: 280px;
      ${isRTL ? 'margin-left' : 'margin-right'}: 0;
    }
    
    /* Smooth transitions for RTL switch */
    .mo3ti-sidebar,
    .mo3ti-main,
    .mo3ti-content {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
  `;

  // ============ MAIN RENDER ============
  return (
    <div style={containerStyles}>
      {/* Background Elements */}
      <div style={backgroundOverlayStyles} />
      <div style={decorativeContainerStyles}>
        <div style={circle1Styles} />
        <div style={circle2Styles} />
      </div>

      {/* Sidebar with Topbar */}
      <SidebarWithTopbar
        navItems={navItems}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        user={user}
        notifications={notifications}
        handleLogout={handleLogout}
        handleSwitchManager={handleSwitchManager}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        themeToggle={renderThemeToggle()}
        languageToggle={renderLanguageToggle()}
        isRTL={isRTL}
        viewModeSwitcher={
          <ViewTypeSwitcher
            viewType={viewType}
            onSelect={(type) => {
              // keep page navigation/logic centralized here while storage writes happen in the component
              handleViewTypeChange(type);
            }}
            hasAdminLevel={() => hasAdminLevel()}
            role={profile?.role || localStorage.getItem('role')}
          />
        }
      >
        <div style={{
          padding: "0",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          marginTop: 40,
        }}>
          <Routes>
            <Route path="/" element={<ProfessionalProfile />} />
            <Route path="/professionalProfile" element={<ProfessionalProfile />} />
            <Route path="/salary" element={<Mo3tiSalaryPage {...pageProps} />} />
            <Route path="/attendance" element={<Mo3tiAttendancePage {...pageProps} />} />
            <Route path="/escalation" element={<EscalationPage {...pageProps} />} />
            <Route path="/groups" element={<GroupsManagement />} />
            <Route path="/employee-services" element={<Mo3tiDocumentsPage {...pageProps} />} />
            <Route path="/calendar" element={<Calendar {...pageProps} />} />
            <Route path="/leave-management" element={<Mo3tiLeavePage {...pageProps} />} />
            <Route path="/settings" element={<Mo3tiSettingsPage {...pageProps} toggleTheme={toggleTheme} />} />
          </Routes>
        </div>
      </SidebarWithTopbar>

      {/* Global Styles */}
      <style>{globalStyles}</style>
    </div>
  );
};

export default Mo3tiUserProfile;
